import React, { useCallback, useEffect, useState } from 'react';
import {
    fetchClientDebugAnalytics,
    fetchClientDebugConfig,
    fetchClientDebugJobs,
    fetchClientDebugPlaybooks,
    fetchClientDebugStatus,
} from '../api/restapi';
import {
    CATEGORICAL_SLOTS, NEUTRAL_COLOR, OTHER_CODE, OUTCOME_LABELS, asList, asObject, executionPhaseLabel,
    failureReasonLabel, formatDuration,
    formatTimestamp, outcomeNote, outcomeOf, pieSegments, playbookLabel, reasonLabel, skipCodeLabel, sourceLabel,
    sourceTypeLabel, stageLabel, titleCase,
} from '../utils/clientDebugFormat';
import SvgPieChart from '../reports/SvgPieChart';
import { DeviceTimeline, JobTimelineSection } from './ClientDebugTimeline';
import { EnvironmentConfig, LogPickup } from './ClientDebugPickup';
import '../assets/AllTabsCommon.css';
import '../assets/ClientDebugAnalytics.css';

const KPI_RANGES = [7, 30, 90];

// Fixed slot per known code (see CATEGORICAL_SLOTS): a code keeps its color whatever its count, and no two codes of
// one chart share a color. No Logs Found has its own slot so it reads apart from pipeline failures. Eight slots, so
// rarer codes (an unknown playbook is refused at startup) are left to take a spare slot or fold into Other.
const [BLUE, ORANGE, AQUA, YELLOW, MAGENTA, GREEN, VIOLET, RED] = CATEGORICAL_SLOTS;
const FAILURE_REASON_COLORS = {
    NO_LOGS_FOUND: YELLOW,
    EMAIL_FAILED: BLUE,
    MAX_JOB_DURATION: ORANGE,
    WORKER_TIMEOUT: VIOLET,
    ALL_ANALYSES_FAILED: RED,
    STEP_ERROR: AQUA,
    WORKER_STALLED: MAGENTA,
    WORKFLOW_ERROR: GREEN,
    UNKNOWN: NEUTRAL_COLOR,
};
const LOG_ANALYSIS_COLORS = {
    LOGTALK_HTTP_ERROR: BLUE,
    LOGTALK_TIMEOUT: ORANGE,
    LOGTALK_FAILED: AQUA,
    LOGTALK_BAD_RESPONSE: YELLOW,
    LOGTALK_CATEGORY_MISSING: MAGENTA,
    LOGTALK_NOT_CONFIGURED: GREEN,
    UNKNOWN: NEUTRAL_COLOR,
};
const LOG_SKIP_COLORS = {
    NO_LOGS: BLUE,
    NOT_REQUESTED: ORANGE,
    ALREADY_DONE: AQUA,
    NO_ANALYSIS_OUTPUT: YELLOW,
    SINGLE_ANALYSIS: MAGENTA,
    INTERRUPTED: GREEN,
    REUSED: VIOLET,
    UNKNOWN: NEUTRAL_COLOR,
};
const SOURCE_TYPE_COLORS = {
    AUTOMATED: BLUE,
    MANUAL: ORANGE,
    UNSPECIFIED: NEUTRAL_COLOR,
};

// Generic artifact wording: callers do not need to know WBC-specific log names.
const ARTIFACT_LABELS = {
    CRASH_DUMP: 'Crash dumps',
    ODL: 'Logs',
    AD: 'Logs',
    AD_LOG: 'Logs',
    LOG: 'Logs',
};

function artifactTypeLabels(value) {
    const labels = asList(value)
        .map((item) => (typeof item === 'string' ? item : item?.type || item?.name))
        .filter(Boolean)
        .map((type) => ARTIFACT_LABELS[String(type).toUpperCase()] || titleCase(type));
    return [...new Set(labels)];
}

function statusCount(analytics, status, legacyKey) {
    const counts = asObject(analytics.statusCounts);
    return Number(counts[status] ?? analytics[legacyKey] ?? 0) || 0;
}

// The service sends outcomeCounts (success / failure / inProgress, with the degraded and in-progress splits). Without
// it, fold the status counts the same way: Success = COMPLETED + PARTIAL, Failure = FAILED + NOT_DEBUGGABLE,
// In Progress = WAITING_FOR_LOGS + QUEUED + RUNNING.
function summarizeStatuses(analytics) {
    const outcomes = asObject(analytics.outcomeCounts);
    if (Object.keys(outcomes).length > 0) {
        return {
            total: Number(analytics.totalJobs) || 0,
            success: Number(outcomes.success) || 0,
            partial: Number(outcomes.degraded) || 0,
            failure: Number(outcomes.failure) || 0,
            inProgress: Number(outcomes.inProgress) || 0,
            waiting: Number(outcomes.waitingForLogs) || 0,
            analyzing: Number(outcomes.analyzing) || 0,
        };
    }
    const partial = statusCount(analytics, 'PARTIAL', 'partialCount');
    const waiting = statusCount(analytics, 'WAITING_FOR_LOGS');
    const analyzing = statusCount(analytics, 'QUEUED') + statusCount(analytics, 'RUNNING');
    const success = statusCount(analytics, 'COMPLETED', 'completedCount') + partial;
    const failure = statusCount(analytics, 'FAILED', 'failedCount') + statusCount(analytics, 'NOT_DEBUGGABLE', 'notDebuggableCount');
    const inProgress = waiting + analyzing;
    const reported = Number(analytics.totalJobs);
    return {
        total: Number.isFinite(reported) && analytics.totalJobs != null ? reported : success + failure + inProgress,
        success, partial, failure, inProgress, waiting, analyzing,
    };
}

function formatNumber(value) {
    if (value == null || value === '') return '—';
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString() : '—';
}

function formatCurrency(value) {
    if (value == null || value === '') return '—';
    const number = Number(value);
    return Number.isFinite(number) ? `$${number.toFixed(2)}` : '—';
}

function safeLogicalLocation(value) {
    if (typeof value !== 'string' || !value.startsWith('s3://') || value.includes('?') || value.includes('#')) {
        return 'Unavailable';
    }
    return value;
}

// ISO-8601 durations from the service ("PT30M", "PT1H30M") -> "30 minutes", "1 hour 30 minutes".
function formatWindowValue(value) {
    if (value == null || value === '') return null;
    const text = String(value);
    const iso = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/i.exec(text);
    if (iso && (iso[1] || iso[2] || iso[3])) {
        const unit = (count, name) => (count ? `${Number(count)} ${name}${Number(count) === 1 ? '' : 's'}` : null);
        return [unit(iso[1], 'hour'), unit(iso[2], 'minute'), unit(iso[3], 'second')].filter(Boolean).join(' ');
    }
    return text.replace(/^PT/, '').toLowerCase();
}

// The period around the issue time that the debug looks at. It applies to logs WBC collected and to logs a caller supplies.
function analysisTimeWindow(playbook) {
    const window = playbook.collectionWindow || playbook.logWindow;
    if (typeof window === 'string') return window;
    if (window && typeof window === 'object') {
        const before = formatWindowValue(window.before);
        const after = formatWindowValue(window.after);
        if (before || after) return `${before || '—'} before · ${after || '—'} after issue time`;
    }
    if (playbook.collectionWindowMinutes && typeof playbook.collectionWindowMinutes === 'object') {
        const before = formatWindowValue(playbook.collectionWindowMinutes.before);
        const after = formatWindowValue(playbook.collectionWindowMinutes.after);
        if (before || after) return `${before || '—'} minutes before · ${after || '—'} minutes after issue time`;
    }
    return 'Not specified';
}

function artifactSummary(artifacts) {
    const values = asList(artifacts);
    const logs = values.filter((artifact) => artifact.kind === 'log').length;
    const files = values.filter((artifact) => artifact.kind === 'file').length;
    return `${logs} log${logs === 1 ? '' : 's'}${files ? ` · ${files} file${files === 1 ? '' : 's'}` : ''}`;
}

function emailOutcome(job) {
    if (job.email?.sentAt) return 'sent';
    if (job.error?.code === 'EMAIL_FAILED' || job.email?.status === 'FAILED') return 'failed';
    return 'none';
}

function MetricCard({ label, value, detail, className = '' }) {
    return (
        <div className={`client-debug-metric-card${className ? ` ${className}` : ''}`}>
            <span>{label}</span>
            <strong>{value}</strong>
            {detail && <small>{detail}</small>}
        </div>
    );
}

function DurationValues({ metrics }) {
    return (
        <div className="client-debug-summary-values">
            <div><span>Avg</span><strong>{formatDuration(metrics?.averageMs)}</strong></div>
            <div><span>Median</span><strong>{formatDuration(metrics?.medianMs)}</strong></div>
            <div><span>P95</span><strong>{formatDuration(metrics?.p95Ms)}</strong></div>
        </div>
    );
}

function AnalyticsSummary({ analytics, loading, error, rangeDays, onRangeChange }) {
    const duration = asObject(analytics.duration);
    const usage = asObject(analytics.usage);
    const usageTotal = asObject(usage.total);
    const logs = asObject(analytics.logs);
    const devices = asObject(analytics.devices);
    const email = asObject(analytics.email);
    const stageDurations = asObject(analytics.stageDurations);
    const playbookUsage = asObject(analytics.playbookUsage);
    const summary = summarizeStatuses(analytics);

    return (
        <section className="client-debug-summary" aria-label="Debug KPIs">
            <div className="client-debug-section-heading">
                <div>
                    <h2>Debug KPIs</h2>
                </div>
                <div className="client-debug-range-controls" role="group" aria-label="KPI range">
                    {KPI_RANGES.map((range) => (
                        <button
                            type="button"
                            key={range}
                            className={`client-debug-range-button${rangeDays === range ? ' active' : ''}`}
                            aria-pressed={rangeDays === range}
                            onClick={() => onRangeChange(range)}
                        >
                            {range} days
                        </button>
                    ))}
                </div>
            </div>

            {error && <div className="client-debug-load-error">{error}</div>}
            {loading ? <div className="client-debug-summary-loading">Loading KPI summary…</div> : (
                <>
                    <div className="client-debug-metric-grid">
                        <MetricCard label="Total Debugs" value={formatNumber(summary.total)} />
                        <MetricCard
                            label="Success"
                            value={formatNumber(summary.success)}
                            detail={summary.partial > 0 ? `incl. ${formatNumber(summary.partial)} with partial analysis` : null}
                            className="success"
                        />
                        <MetricCard label="Failure" value={formatNumber(summary.failure)} className="danger" />
                        <MetricCard
                            label="In Progress"
                            value={formatNumber(summary.inProgress)}
                            detail={`${formatNumber(summary.waiting)} waiting for logs · ${formatNumber(summary.analyzing)} analyzing`}
                        />
                        <MetricCard
                            label="No Logs Found"
                            value={formatNumber(analytics.noLogCount)}
                            detail="finished without any logs to analyze"
                            className="warning"
                        />
                        <MetricCard label="Unique devices" value={formatNumber(devices.unique)} />
                        <MetricCard
                            label="Retried"
                            value={formatNumber(analytics.retriedCount)}
                            detail="claimed again after a lost or stalled worker"
                        />
                    </div>

                    <div className="client-debug-summary-grid">
                        <div className="client-debug-summary-card">
                            <h3>Debug Pipeline Execution Duration</h3>
                            <DurationValues metrics={duration} />
                        </div>
                        <div className="client-debug-summary-card">
                            <h3>Logs Analyzed</h3>
                            <div className="client-debug-summary-values">
                                <div><span>Logs analyzed</span><strong>{formatNumber(logs.analyzed)}</strong></div>
                            </div>
                            <p className="client-debug-muted">Logs that were analyzed for finalized debugs in this period.</p>
                        </div>
                        <div className="client-debug-summary-card">
                            <h3>Email</h3>
                            <div className="client-debug-summary-values">
                                <div><span>Email Successfully Sent</span><strong>{formatNumber(email.sent)}</strong></div>
                                <div><span>Email Send Failure</span><strong>{formatNumber(email.failed)}</strong></div>
                            </div>
                        </div>
                        <div className="client-debug-summary-card">
                            <h3>Usage</h3>
                            <div className="client-debug-summary-values">
                                <div><span>LLM calls</span><strong>{formatNumber(usageTotal.llmCalls)}</strong></div>
                                <div><span>Input tokens</span><strong>{formatNumber(usageTotal.inputTokens)}</strong></div>
                                <div><span>Output tokens</span><strong>{formatNumber(usageTotal.outputTokens)}</strong></div>
                                <div><span>Cost</span><strong>{formatCurrency(usageTotal.costUsd)}</strong></div>
                            </div>
                        </div>
                    </div>

                    <div className="client-debug-summary-grid">
                        <div className="client-debug-summary-card">
                            <h3>Pipeline Stages Duration</h3>
                            <p className="client-debug-muted">Breakdown of the Debug Pipeline Execution Duration by stage.</p>
                            {Object.keys(stageDurations).length === 0 ? <p className="client-debug-muted">No stage duration data.</p> : (
                                <div className="client-debug-stage-metrics">
                                    {Object.entries(stageDurations).map(([stage, metrics]) => (
                                        <div key={stage}>
                                            <strong>{stageLabel(stage)}</strong>
                                            <span>Avg {formatDuration(metrics?.averageMs)} · Median {formatDuration(metrics?.medianMs)} · P95 {formatDuration(metrics?.p95Ms)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="client-debug-summary-card">
                            <h3>Playbook usage</h3>
                            {Object.keys(playbookUsage).length === 0 ? <p className="client-debug-muted">No playbook usage data.</p> : (
                                <div className="client-debug-usage-list">
                                    {Object.entries(playbookUsage).map(([playbook, count]) => (
                                        <div key={playbook}><span>{playbookLabel(playbook)}</span><strong>{formatNumber(count)}</strong></div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <FailureReasonDistribution analytics={analytics} />
                    <SourceDistribution analytics={analytics} />
                </>
            )}
        </section>
    );
}

function DistributionPieChart({ title, countsObj, labelFn, colorPalette }) {
    const segments = pieSegments(asObject(countsObj), colorPalette);
    if (segments.length === 0) {
        return null;
    }
    return (
        <div className="client-debug-summary-card client-debug-failure-chart-card">
            <h3>{title}</h3>
            <SvgPieChart
                labels={segments.map(({ code }) => (code === OTHER_CODE ? 'Other' : labelFn(code)))}
                values={segments.map(({ value }) => value)}
                colors={segments.map(({ color }) => color)}
                size={190}
            />
        </div>
    );
}

function hasCounts(counts) {
    return Object.values(asObject(counts)).some((v) => Number(v) > 0);
}

function FailureReasonDistribution({ analytics }) {
    if (!hasCounts(analytics.failureReasonCounts) && !hasCounts(analytics.logAnalysisFailureCounts)
        && !hasCounts(analytics.logAnalysisSkippedCounts)) {
        return null;
    }
    return (
        <div className="client-debug-summary-grid client-debug-failure-distribution">
            <DistributionPieChart
                title="Failure Reason Distribution"
                countsObj={analytics.failureReasonCounts}
                labelFn={failureReasonLabel}
                colorPalette={FAILURE_REASON_COLORS}
            />
            <DistributionPieChart
                title="Log Analysis Failures"
                countsObj={analytics.logAnalysisFailureCounts}
                labelFn={failureReasonLabel}
                colorPalette={LOG_ANALYSIS_COLORS}
            />
            <DistributionPieChart
                title="Log Analysis Skipped"
                countsObj={analytics.logAnalysisSkippedCounts}
                labelFn={skipCodeLabel}
                colorPalette={LOG_SKIP_COLORS}
            />
        </div>
    );
}

/** Who asked for the debugs: the trigger type as a pie, and the free-text calling systems as a ranked list. */
function SourceDistribution({ analytics }) {
    const systems = Object.entries(asObject(analytics.sourceSystemCounts))
        .map(([system, count]) => [system, Number(count) || 0])
        .filter(([, count]) => count > 0)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    if (!hasCounts(analytics.sourceTypeCounts) && systems.length === 0) {
        return null;
    }
    const total = systems.reduce((sum, [, count]) => sum + count, 0);
    return (
        <div className="client-debug-summary-grid client-debug-failure-distribution">
            <DistributionPieChart
                title="Debugs by Trigger"
                countsObj={analytics.sourceTypeCounts}
                labelFn={sourceTypeLabel}
                colorPalette={SOURCE_TYPE_COLORS}
            />
            {systems.length > 0 && (
                <div className="client-debug-summary-card client-debug-source-card">
                    <h3>Debugs by Caller</h3>
                    <table className="client-debug-source-table">
                        <thead><tr><th scope="col">Caller</th><th scope="col">Debugs</th><th scope="col">Share</th></tr></thead>
                        <tbody>
                            {systems.map(([system, count]) => (
                                <tr key={system}>
                                    <td>{system === 'unspecified' ? 'Not specified' : system}</td>
                                    <td>{formatNumber(count)}</td>
                                    <td>{total ? `${Math.round((count / total) * 100)}%` : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function PlaybooksSection({ playbooks, loading, error }) {
    return (
        <section className="client-debug-playbooks" aria-label="Client Debug playbooks">
            <div className="client-debug-section-heading">
                <div>
                    <h2>Playbooks</h2>
                </div>
            </div>
            {error && <div className="client-debug-load-error">{error}</div>}
            {loading ? <div className="client-debug-summary-loading">Loading playbooks…</div> : playbooks.length === 0 ? (
                <div className="client-debug-empty">No playbook metadata is available.</div>
            ) : (
                <div className="client-debug-playbook-grid">
                    {playbooks.map((playbook) => {
                        const permitted = playbook.permittedArtifactTypes
                            || playbook.permittedArtifacts
                            || playbook.artifactTypes
                            || playbook.logs;
                        return (
                            <article className="client-debug-playbook-card" key={playbook.name || playbook.displayName}>
                                <h3>{playbook.displayName || playbook.name ? playbookLabel(playbook.displayName || playbook.name) : 'Unnamed playbook'}</h3>
                                <p>{playbook.description || 'No description provided.'}</p>
                                <dl>
                                    <div><dt>Permitted Artifacts</dt><dd>{artifactTypeLabels(permitted).join(', ') || '—'}</dd></div>
                                    <div><dt>Time window analyzed</dt><dd>{analysisTimeWindow(playbook)}</dd></div>
                                </dl>
                            </article>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

function ArtifactDetails({ artifact }) {
    const analyzed = Boolean(artifact.analysisId || artifact.analyzed);
    const staged = artifact.staged === true ? 'Staged' : artifact.staged === false ? 'Not staged' : 'Not recorded';
    return (
        <div className="client-debug-artifact">
            <div><span>Logical location</span><strong>{safeLogicalLocation(artifact.uri)}</strong></div>
            <div><span>Artifact type</span><strong>{artifact.artifactType || (artifact.kind === 'file' ? 'Uploaded file' : 'Log')}</strong></div>
            <div><span>Collection time</span><strong>{formatTimestamp(artifact.collectedAt)}</strong></div>
            <div><span>Staging state</span><strong>{staged}</strong></div>
            <div><span>Analyzed state</span><strong>{analyzed ? 'Analyzed' : 'Not analyzed'}</strong></div>
        </div>
    );
}

function JobDetails({ job, userInfo }) {
    const steps = asList(job.steps);
    const artifacts = asList(job.artifacts);
    const outcome = emailOutcome(job);
    const recipients = asList(job.email?.to).filter((address) => typeof address === 'string');
    return (
        <div className="client-debug-job-details">
            <div className="client-debug-detail-grid">
                <div><span>Issue time</span><strong>{formatTimestamp(job.issueTime)}</strong></div>
                <div><span>Playbook</span><strong>{playbookLabel(job.playbook)}</strong></div>
                <div><span>Email delivery</span><strong>{outcome === 'sent' ? `Sent ${formatTimestamp(job.email.sentAt)}` : outcome === 'failed' ? 'Send failure' : 'Not recorded'}</strong></div>
                {recipients.length > 0 && <div><span>Email sent to</span><strong>{recipients.join(', ')}</strong></div>}
                <div><span>Source</span><strong>{sourceLabel(job.source) || 'Not specified'}</strong></div>
                {job.source?.reference && <div><span>Source reference</span><strong>{job.source.reference}</strong></div>}
                {job.requestedBy && <div><span>Requested by</span><strong>{job.requestedBy}</strong></div>}
                {job.attempt > 0 && <div><span>Attempt</span><strong>{job.attempt}{job.attempt > 1 ? ' (retried)' : ''}</strong></div>}
                {job.phase && <div><span>Now</span><strong>{executionPhaseLabel(job.phase)}</strong></div>}
                {job.progressAt && <div><span>Last progress</span><strong>{formatTimestamp(job.progressAt)}</strong></div>}
            </div>

            {job.statusReason && <div className="client-debug-reason"><span>Status detail</span>{reasonLabel(job.statusReason)}</div>}
            {job.error?.code && <div className="client-debug-error"><span>{job.error.code}</span>{job.error.message || 'The job reported an error.'}</div>}

            <div className="client-debug-detail-section">
                <h4>Timeline</h4>
                <JobTimelineSection userInfo={userInfo} jobId={job.jobId} />
            </div>

            <div className="client-debug-detail-section">
                <h4>Log pickup</h4>
                <LogPickup job={job} />
            </div>

            <div className="client-debug-detail-section">
                <h4>Run stages</h4>
                {steps.length === 0 ? <p className="client-debug-muted">No stage updates recorded yet.</p> : (
                    <div className="client-debug-stage-list">
                        {steps.map((step) => (
                            <div className="client-debug-stage" key={`${step.seq}-${step.name}`}>
                                <div className="client-debug-stage-main">
                                    <span className="client-debug-stage-seq">{step.seq}</span>
                                    <strong>{stageLabel(step.name)}</strong>
                                    <span className={`client-debug-stage-status ${String(step.status || '').toLowerCase()}`}>{step.status}</span>
                                </div>
                                <div className="client-debug-stage-meta">
                                    {[step.detail, step.durationMs != null ? formatDuration(step.durationMs) : null].filter(Boolean).join(' · ') || '—'}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="client-debug-detail-section">
                <h4>Evidence</h4>
                <p className="client-debug-muted">{artifactSummary(artifacts)} recorded. Analysis output remains in the result email.</p>
                {artifacts.length > 0 && (
                    <div className="client-debug-artifact-list">
                        {artifacts.map((artifact, index) => (
                            <ArtifactDetails artifact={artifact} key={`${artifact.kind || 'artifact'}-${artifact.uri || index}`} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function ClientDebugAnalytics({ userInfo }) {
    const [jobs, setJobs] = useState([]);
    const [playbooks, setPlaybooks] = useState([]);
    const [analytics, setAnalytics] = useState({});
    const [loading, setLoading] = useState(true);
    const [analyticsLoading, setAnalyticsLoading] = useState(true);
    const [playbooksLoading, setPlaybooksLoading] = useState(true);
    const [error, setError] = useState(null);
    const [analyticsError, setAnalyticsError] = useState(null);
    const [playbooksError, setPlaybooksError] = useState(null);
    const [rangeDays, setRangeDays] = useState(30);
    const [expandedJobId, setExpandedJobId] = useState(null);
    const [timelineDevice, setTimelineDevice] = useState('');
    const [enabled, setEnabled] = useState(null);
    const [config, setConfig] = useState(null);
    const [configLoading, setConfigLoading] = useState(true);
    const [configError, setConfigError] = useState(null);

    const loadJobs = useCallback(async () => {
        setLoading(true);
        const result = await fetchClientDebugJobs({ userEmail: userInfo });
        setJobs(result.jobs);
        setError(result.errorMessage || null);
        setLoading(false);
    }, [userInfo]);

    const loadAnalytics = useCallback(async () => {
        setAnalyticsLoading(true);
        const result = await fetchClientDebugAnalytics({ userEmail: userInfo, rangeDays });
        setAnalytics(result.analytics);
        setAnalyticsError(result.errorMessage || null);
        setAnalyticsLoading(false);
    }, [rangeDays, userInfo]);

    const loadPlaybooks = useCallback(async () => {
        setPlaybooksLoading(true);
        const result = await fetchClientDebugPlaybooks({ userEmail: userInfo });
        setPlaybooks(result.playbooks);
        setPlaybooksError(result.errorMessage || null);
        setPlaybooksLoading(false);
    }, [userInfo]);

    const loadConfig = useCallback(async () => {
        setConfigLoading(true);
        const result = await fetchClientDebugConfig({ userEmail: userInfo });
        setConfig(result.config);
        setConfigError(result.errorMessage || null);
        setConfigLoading(false);
    }, [userInfo]);

    const refresh = useCallback(async () => {
        await Promise.all([loadJobs(), loadAnalytics(), loadPlaybooks(), loadConfig()]);
    }, [loadAnalytics, loadConfig, loadJobs, loadPlaybooks]);

    useEffect(() => { loadJobs(); }, [loadJobs]);
    useEffect(() => { loadAnalytics(); }, [loadAnalytics]);
    useEffect(() => { loadPlaybooks(); }, [loadPlaybooks]);
    useEffect(() => { loadConfig(); }, [loadConfig]);
    useEffect(() => {
        let active = true;
        fetchClientDebugStatus({ userEmail: userInfo }).then((result) => {
            if (active) setEnabled(result.enabled);
        });
        return () => { active = false; };
    }, [userInfo]);

    const refreshDisabled = loading || analyticsLoading || playbooksLoading;

    return (
        <div className="client-debug-analytics">
            <div className="client-debug-page-header">
                <div>
                    <h1>Debug</h1>
                </div>
                <button type="button" className="client-debug-refresh" onClick={refresh} disabled={refreshDisabled}>
                    {refreshDisabled ? 'Refreshing…' : 'Refresh'}
                </button>
            </div>

            {enabled === false && (
                <div className="client-debug-disabled-banner" role="status">
                    Debug is disabled in this environment: no new debugs are accepted or processed. History below stays available.
                </div>
            )}

            <AnalyticsSummary
                analytics={analytics}
                loading={analyticsLoading}
                error={analyticsError}
                rangeDays={rangeDays}
                onRangeChange={setRangeDays}
            />

            <PlaybooksSection playbooks={playbooks} loading={playbooksLoading} error={playbooksError} />

            <EnvironmentConfig config={config} loading={configLoading} error={configError} />

            <DeviceTimeline userInfo={userInfo} deviceId={timelineDevice} />

            <section className="client-debug-activity" aria-label="Debug Activity">
            <div className="client-debug-section-heading">
                <div>
                    <h2>Debug Activity</h2>
                    <p>Status and execution metadata for each debug. RCA and analysis output is delivered by email.</p>
                </div>
            </div>

            {error && <div className="client-debug-load-error">{error}</div>}
            {loading && jobs.length === 0 && <div className="client-debug-empty">Loading debug activity…</div>}
            {!loading && !error && jobs.length === 0 && <div className="client-debug-empty">No debugs have been recorded.</div>}

            {jobs.length > 0 && (
                <div className="client-debug-table-wrap">
                    <table className="client-debug-table">
                        <thead>
                            <tr>
                                {['Created', 'Debug ID', 'Device', 'Source', 'Status', 'Playbook', 'Email', 'Details'].map((heading) => (
                                    <th scope="col" key={heading}>{heading}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map((job) => {
                                const expanded = expandedJobId === job.jobId;
                                return (
                                    <React.Fragment key={job.jobId}>
                                        <tr className={expanded ? 'expanded' : ''}>
                                            <td>{formatTimestamp(job.createdAt)}</td>
                                            <td><code title={job.jobId}>{String(job.jobId).slice(0, 8)}</code></td>
                                            <td>{job.deviceId
                                                ? <button type="button" className="client-debug-link" onClick={() => setTimelineDevice(job.deviceId)}>{job.deviceId}</button>
                                                : '—'}</td>
                                            <td>
                                                {sourceLabel(job.source) || job.requestedBy || 'Not specified'}
                                                {sourceLabel(job.source) && job.requestedBy && <small className="client-debug-status-note">{job.requestedBy}</small>}
                                            </td>
                                            <td>
                                                <span className={`client-debug-status ${String(job.status || '').toLowerCase()}`}>{OUTCOME_LABELS[outcomeOf(job)]}</span>
                                                {outcomeNote(job) && <small className="client-debug-status-note">{outcomeNote(job)}</small>}
                                            </td>
                                            <td>{playbookLabel(job.playbook)}</td>
                                            <td>{{ sent: 'Sent', failed: 'Send failure', none: '—' }[emailOutcome(job)]}</td>
                                            <td><button type="button" className="client-debug-details-button" onClick={() => setExpandedJobId(expanded ? null : job.jobId)}>{expanded ? 'Hide' : 'View'}</button></td>
                                        </tr>
                                        {expanded && <tr className="client-debug-expanded-row"><td colSpan="8"><JobDetails job={job} userInfo={userInfo} /></td></tr>}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            </section>
        </div>
    );
}

export default ClientDebugAnalytics;
